/**
 * The command palette's catalogue: every destination and action Ctrl K can reach.
 *
 * Static data on purpose — the palette does no lookups and spends no quota. Each
 * command carries an `effect`, a discriminated union, so an action can later grow
 * a payload (e.g. "open the create form") without reshaping the list or the UI.
 */

import {
  KNOWLEDGE_BASE_SECTIONS,
  knowledgeBaseSectionPath,
  type KnowledgeBaseSection,
} from '../../features/knowledge-base/sections';
import { NEWS_SOURCES_PATH } from '../../features/news/newsRoutes';
import type { ResolvedTheme } from '../../lib/theme';

export type CommandGroupKey = 'navigate' | 'action';

/**
 * What running a command does. Navigation is a route; sign-out is delegated to
 * Supabase; set-theme stores this device's theme. The palette hides the
 * set-theme command for the theme already on screen.
 */
export type CommandEffect =
  | { kind: 'navigate'; to: string }
  | { kind: 'sign-out' }
  | { kind: 'set-theme'; theme: ResolvedTheme };

export interface Command {
  /** Stable, used for React keys and the option's DOM id. */
  id: string;
  /** Sentence case, verb-first for actions. */
  label: string;
  group: CommandGroupKey;
  /** Extra match terms — searched alongside the label, never displayed. */
  keywords: readonly string[];
  effect: CommandEffect;
}

/** Group order is display order, and therefore also arrow-key order. */
export const COMMAND_GROUPS: readonly { key: CommandGroupKey; label: string }[] = [
  { key: 'navigate', label: 'Go to' },
  { key: 'action', label: 'Actions' },
];

/** Search terms per knowledge-base section, keyed so a new section fails the build. */
const SECTION_KEYWORDS: Record<KnowledgeBaseSection, readonly string[]> = {
  'brand-identity': [
    'kb',
    'knowledge base',
    'brand profile',
    'brand',
    'tone',
    'tagline',
    'colours',
    'colors',
    'fonts',
    'logo',
    'never say',
    'words to avoid',
  ],
  looks: [
    'kb',
    'knowledge base',
    'brand profile',
    'looks',
    'visual styles',
    'themes',
    'styles',
    'templates',
    'gallery',
    'carousel',
    'slides',
  ],
  voice: [
    'kb',
    'knowledge base',
    'brand profile',
    'brand',
    'voice',
    'example posts',
    'tone',
    'samples',
    'writing',
  ],
  products: ['kb', 'knowledge base', 'brand profile', 'catalogue', 'catalog', 'services', 'offers'],
  assets: [
    'kb',
    'knowledge base',
    'brand profile',
    'images',
    'photos',
    'files',
    'uploads',
    'logo',
    'media',
  ],
  'proof-points': [
    'kb',
    'knowledge base',
    'brand profile',
    'evidence',
    'claims',
    'stats',
    'testimonials',
    'reviews',
  ],
  inspiration: [
    'kb',
    'knowledge base',
    'brand profile',
    'inspiration',
    'mood board',
    'moodboard',
    'references',
    'style',
    'taste',
  ],
  'brand-memory': [
    'kb',
    'knowledge base',
    'brand profile',
    'memory',
    'memories',
    'learned',
    'learn',
    'preferences',
    'suggestions',
  ],
  integrations: [
    'kb',
    'knowledge base',
    'brand profile',
    'connected',
    'accounts',
    'integrations',
    'linkedin',
    'connect',
    'publish',
    'oauth',
  ],
};

const SECTION_COMMANDS: readonly Command[] = KNOWLEDGE_BASE_SECTIONS.map((section): Command => ({
  id: `go-kb-${section.key}`,
  label: section.label,
  group: 'navigate',
  keywords: SECTION_KEYWORDS[section.key],
  effect: { kind: 'navigate', to: knowledgeBaseSectionPath(section.key) },
}));

/**
 * Actions land on the section that owns the record. They are separate commands
 * rather than aliases of the navigation ones because that is how a user thinks
 * about them, and because each is where a create-form payload will attach.
 */
const ACTION_COMMANDS: readonly Command[] = [
  {
    id: 'new-assistant-chat',
    label: 'New AI assistant chat',
    group: 'action',
    keywords: ['ai', 'assistant', 'agent', 'chat', 'ask', 'planner', 'plan', 'conversation'],
    effect: { kind: 'navigate', to: '/assistant?new=1' },
  },
  {
    id: 'plan-post',
    label: 'Plan a post',
    group: 'action',
    keywords: ['calendar', 'schedule', 'month', 'planned', 'new', 'upcoming'],
    effect: { kind: 'navigate', to: '/calendar' },
  },
  {
    id: 'add-product',
    label: 'Add product',
    group: 'action',
    keywords: ['new', 'create', 'catalogue', 'catalog', 'service', 'offer', 'kb'],
    effect: { kind: 'navigate', to: knowledgeBaseSectionPath('products') },
  },
  {
    id: 'add-voice-example',
    label: 'Add example post',
    group: 'action',
    keywords: ['new', 'create', 'brand voice', 'example', 'tone', 'sample', 'kb'],
    effect: { kind: 'navigate', to: knowledgeBaseSectionPath('voice') },
  },
  {
    id: 'add-proof-point',
    label: 'Add testimonial or result',
    group: 'action',
    keywords: ['new', 'create', 'evidence', 'claim', 'stat', 'testimonial', 'kb'],
    effect: { kind: 'navigate', to: knowledgeBaseSectionPath('proof-points') },
  },
  {
    id: 'upload-asset',
    label: 'Upload image or logo',
    group: 'action',
    keywords: ['new', 'add', 'image', 'photo', 'file', 'logo', 'media', 'kb'],
    effect: { kind: 'navigate', to: knowledgeBaseSectionPath('assets') },
  },
  {
    id: 'manage-news-sources',
    label: 'Manage news sources',
    group: 'action',
    keywords: ['news', 'feeds', 'rss', 'atom', 'sources', 'industry', 'publications', 'newsfeed'],
    effect: { kind: 'navigate', to: NEWS_SOURCES_PATH },
  },
  {
    id: 'theme-dark',
    label: 'Switch to dark theme',
    group: 'action',
    keywords: ['dark mode', 'night', 'theme', 'appearance', 'colours', 'colors'],
    effect: { kind: 'set-theme', theme: 'dark' },
  },
  {
    id: 'theme-light',
    label: 'Switch to light theme',
    group: 'action',
    keywords: ['light mode', 'day', 'theme', 'appearance', 'colours', 'colors'],
    effect: { kind: 'set-theme', theme: 'light' },
  },
  {
    id: 'sign-out',
    label: 'Sign out',
    group: 'action',
    keywords: ['log out', 'logout', 'leave', 'exit', 'session', 'account'],
    effect: { kind: 'sign-out' },
  },
];

export const COMMANDS: readonly Command[] = [
  {
    id: 'go-dashboard',
    label: 'Home',
    group: 'navigate',
    keywords: ['dashboard', 'overview', 'readiness', 'start'],
    effect: { kind: 'navigate', to: '/' },
  },
  {
    id: 'go-generation',
    label: 'Create post',
    group: 'navigate',
    keywords: ['generate', 'generation', 'new post', 'create', 'caption', 'carousel', 'brief', 'vibe'],
    effect: { kind: 'navigate', to: '/generation' },
  },
  {
    id: 'go-assistant',
    label: 'AI assistant',
    group: 'navigate',
    keywords: ['ai', 'assistant', 'agent', 'chat', 'planner', 'plan', 'goals', 'help'],
    effect: { kind: 'navigate', to: '/assistant' },
  },
  {
    id: 'go-calendar',
    label: 'Calendar',
    group: 'navigate',
    keywords: ['plan', 'planner', 'schedule', 'month', 'goals', 'campaigns', 'content calendar'],
    effect: { kind: 'navigate', to: '/calendar' },
  },
  {
    id: 'go-news',
    label: 'News',
    group: 'navigate',
    keywords: ['industry', 'headlines', 'articles', 'feed', 'rss', 'trends', 'newsfeed'],
    effect: { kind: 'navigate', to: '/news' },
  },
  {
    id: 'go-knowledge-base',
    label: 'Brand profile',
    group: 'navigate',
    keywords: ['kb', 'brand', 'business', 'grounding'],
    effect: { kind: 'navigate', to: '/knowledge-base' },
  },
  ...SECTION_COMMANDS,
  {
    id: 'go-templates',
    label: 'Templates',
    group: 'navigate',
    keywords: ['looks', 'themes', 'gallery', 'community', 'packs', 'styles'],
    effect: { kind: 'navigate', to: '/templates' },
  },
  {
    id: 'go-history',
    label: 'History',
    group: 'navigate',
    keywords: ['past', 'log', 'archive', 'requests', 'previous', 'published'],
    effect: { kind: 'navigate', to: '/history' },
  },
  {
    id: 'go-settings',
    label: 'Settings',
    group: 'navigate',
    keywords: [
      'account',
      'profile',
      'password',
      'business name',
      'industry',
      'audience',
      'email',
      'appearance',
      'theme',
    ],
    effect: { kind: 'navigate', to: '/settings' },
  },
  ...ACTION_COMMANDS,
];

/**
 * Case-insensitive substring match over label plus keywords. Every whitespace
 * separated token must hit, so "add cal" narrows the way a user expects. No
 * fuzzy matching and no dependency — a wrong guess costs more than a second query.
 */
export function filterCommands(query: string): Command[] {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [...COMMANDS];

  return COMMANDS.filter((command) => {
    const haystack = `${command.label} ${command.keywords.join(' ')}`.toLowerCase();
    return tokens.every((token) => haystack.includes(token));
  });
}
